import { Text } from "@chakra-ui/layout";
import { NavigationReports } from "@components/widgets/AppSubNav";
import { MainLayout } from "@components/widgets/Layout";
import { Trans, t } from "@lingui/macro";
import {
  ReportCategories,
  type ReportCategory,
} from "@src/components/modules/reports/site-map";
import { ModuleTitle } from "@src/components/ui-kit";
import { DemoModule, Modules } from "@src/components/widgets/DemoModule";
import { createFileRoute } from "@tanstack/react-router";

const reportCategories: ReportCategory[] = [
  {
    title: () => t`Project`,
    reports: [
      {
        title: () => t`Project Profit`,
        description: () =>
          t`Insights into project profits generated from agency work, production, and media, calculated by comparing invoiced amounts to received expenses.`,
        href: "/reports/profitability-by-project",
        bg: "pink.100",
        isBeta: true,
      },
      {
        title: () => t`Project Billings`,
        description: () =>
          t`Track billings across all ongoing and finished projects split into work, production, and media over time.`,
        href: "/reports/invoiced-by-project",
        bg: "orange.100",
        isBeta: true,
      },
      {
        title: () => t`Project Billings & Profit`,
        description: () =>
          t`Displays billing of projects in the selected period compared to the profit generated from work done and closed expense items.`,
        href: "/reports/invoiced-and-profitability-by-project",
        bg: "cyan.100",
        isBeta: true,
      },
      {
        title: () => t`Profit & Loss`,
        description: () =>
          t`Displays the profit and loss of the agency, calculated by comparing invoiced amounts to received expenses.`,
        href: "/reports/profit-and-loss",
        bg: "gray.100",
        permissions: ["profitLossReport_query_all"],
      },
      {
        title: () => t`Budget exceeded`,
        description: () => "",
        href: "/reports/budget-exceeded",
        bg: "red.100",
        permissions: [
          "project_read_all",
          "project_read_own",
          "project_read_team",
          "project_viewAllFields",
        ],
      },
    ],
  },
  {
    title: () => t`Brand`,
    reports: [
      {
        title: () => t`Brand Profit`,
        description: () =>
          t`Detailed view of brand profits generated from agency work, production, and media, calculated by comparing invoiced amounts to received expenses.`,
        href: "/reports/profitability-by-brand",
        bg: "purple.100",
        isBeta: true,
      },
      {
        title: () => t`Brand Billings`,
        description: () =>
          t`Track billings of individual brands, split into work, production, and media over time.`,
        href: "/reports/invoiced-by-brand",
        bg: "green.100",
        isBeta: true,
      },
      {
        title: () => t`Brand Billings & Profit`,
        description: () =>
          t`Compares billing in the selected period to the generated profit of each brand, breaking down all revenue streams.`,
        href: "/reports/invoiced-and-profitability-by-brand",
        bg: "jade.100",
        isBeta: true,
      },
    ],
  },
  {
    title: () => t`User`,
    reports: [
      {
        title: () => t`Utilization report`,
        description: () =>
          t`Report on team time scheduling with insights into capacity, tracked hours, time allocation, billable hours, and utilization.`,
        href: "/reports/utilization",
        bg: "blue.100",
      },
      {
        title: () => t`Time reports`,
        description: () =>
          t`Logs of time spent on tasks and projects, with options to download, edit, and move entries.`,
        href: "/reports/time-report",
        bg: "yellow.100",
      },
      {
        title: () => t`Team time breakdown`,
        description: () =>
          t`Monthly summary of tracked hours per user, showing work distribution and including export options.`,
        href: "/reports/user-monthly-report",
        bg: "red.100",
      },
      {
        title: () => t`User profit report`,
        description: () =>
          t`Report showing individual user profitability based on the difference between budgeted and tracked hours, calculated using each user's internal hourly rate.`,
        href: "/reports/user-profit",
        bg: "gray.100",
        permissions: ["utilizationReportProfit_query_all"],
      },
    ],
  },
];

const Page = () => (
  <MainLayout title={t`Reports`} navigation={<NavigationReports />}>
    <DemoModule module={Modules.Reports}>
      <ModuleTitle>
        <Trans>Reports</Trans>
      </ModuleTitle>
      <Text>
        <Trans>
          A collection of all necessary reports to smoothly guide your agency to
          success. Knowledge is power, my dear padawan!
        </Trans>
      </Text>
      <ReportCategories categories={reportCategories} />
    </DemoModule>
  </MainLayout>
);

export const Route = createFileRoute("/reports/")({
  component: Page,
});
